import React, { useRef, useEffect, useState, useContext } from "react";
import Hls from "hls.js";
import UserContext from "./UserContext";

const HLSPlayer = ({ song, onSongEnd, onNext, onLikeUpdate, onPlayUpdate, autoPlay, playOnLoad, isMobile }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const { user } = useContext(UserContext);
  const [playTracked, setPlayTracked] = useState(false);

useEffect(() => {
  if (autoPlay && audioRef.current) {
    audioRef.current.play()
      .then(() => setIsPlaying(true))
      .catch(err => console.error("Autoplay failed:", err));
  }
}, [autoPlay, song]);

useEffect(() => {
  if (!song || !song.file_url) return;

  const audio = audioRef.current;
  if (!audio) return;

  const playLoadedSong = () => {
    if (autoPlay || playOnLoad) {
      audio.play()
        .then(() => setIsPlaying(true))
        .catch(error => {
          console.error("Autoplay failed:", error);
          setIsPlaying(false);
        });
    } else {
      setIsPlaying(false);
    }
  };

  if (Hls.isSupported()) {
    const hls = new Hls();
    hls.loadSource(song.file_url);
    hls.attachMedia(audio);

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      setDuration(audio.duration);
      playLoadedSong();
    });

    return () => hls.destroy();
  } else if (audio.canPlayType("application/vnd.apple.mpegurl")) {
    audio.src = song.file_url;
    audio.addEventListener("loadedmetadata", playLoadedSong, { once: true });

    return () => {
      audio.removeEventListener("loadedmetadata", playLoadedSong);
    };
  }
}, [song?.file_url]);

useEffect(() => {
  setPlayTracked(false);
}, [song?.id]);

useEffect(() => {
  if (audioRef.current) {
    audioRef.current.volume = volume;
  }
}, [volume]);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const toggleLike = async () => {
    if (!user) {
      alert("Please log in to like songs");
      return;
    }

    if (!song || !song.id) return;

    try {
      const response = await fetch(`/api/songs/${song.id}/like/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      // Notify parent component to update the list
      if (onLikeUpdate) {
        onLikeUpdate(song.id, data.like_count, data.liked);
      }
    } catch (error) {
      console.error("Error liking song:", error);
    }
  };

  
  const formatTime = (seconds) => {
  if (!Number.isFinite(seconds)) return "0:00";

  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);

  return `${mins}:${secs.toString().padStart(2, "0")}`;
};


const handleTimeUpdate = () => {
  const audio = audioRef.current;
  if (!audio) return;

  const time = audio.currentTime;
  setCurrentTime(time);

  const threshold = duration
    ? Math.min(30, duration * 0.5)
    : 30;

  if (!playTracked && time >= threshold) {
    setPlayTracked(true);
    trackPlay();
  }
};

  const handleSeek = (e) => {
    const seekTime = Number(e.target.value);
    audioRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  // Check if current song is liked
  const isLiked = song && song.user_has_liked;

  const updateDuration = () => {
  const audio = audioRef.current;
  if (!audio) return;

  const realDuration = audio.duration;

  console.log("audio.duration:", realDuration);

  if (Number.isFinite(realDuration) && realDuration > 0) {
    setDuration(realDuration);
  }
};

const trackPlay = async () => {
  if (!song?.id) return;

  try {
    const res = await fetch(`/api/songs/${song.id}/play/`, {
      method: "POST",
    });

    const data = await res.json();
     
    if (onPlayUpdate) {
      onPlayUpdate(song.id, data.partial_plays);
    }
	  
    console.log("play tracked:", data.partial_plays);
  } catch (err) {
    console.error("play tracking error:", err);
  }
};

const waveformBars = [
  0.10, 0.14, 0.19, 0.13, 0.28, 0.22, 0.36, 0.18,
  0.31, 0.46, 0.27, 0.41, 0.58, 0.34, 0.52, 0.29,
  0.17, 0.63, 0.49, 0.24, 0.71, 0.38, 0.55, 0.32,
  0.21, 0.78, 0.44, 0.26, 0.67, 0.36, 0.59, 0.30,
  0.15, 0.84, 0.48, 0.23, 0.73, 0.42, 0.61, 0.35,
  0.20, 0.69, 0.53, 0.28, 0.76, 0.39, 0.57, 0.33,
  0.16, 0.62, 0.45, 0.25, 0.70, 0.37, 0.54, 0.31,
  0.18, 0.80, 0.47, 0.22, 0.66, 0.43, 0.60, 0.27,
  0.14, 0.51, 0.35, 0.19, 0.64, 0.40, 0.56, 0.30,
  0.12, 0.44, 0.26, 0.17, 0.50, 0.33, 0.46, 0.21
];

const progressRatio = duration ? currentTime / duration : 0;


if (isMobile) {
  return (
    <div style={{
      background: "rgba(0,0,0,0.82)",
      border: "1px solid rgba(255,140,0,0.25)",
      borderRadius: "12px",
      padding: "10px 12px",
      color: "white",
      width: "100%",
      boxSizing: "border-box",
    }}>
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={updateDuration}
        onDurationChange={updateDuration}
        onEnded={onSongEnd}
      />

      {/* TOP ROW - artwork + title + controls */}
      <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
        
        {/* ARTWORK */}
        <img
          src={song?.song_picture || "https://placehold.co/300x300/111111/ff2b63?text=SB"}
          alt="artwork"
          style={{
            width: "60px",
            height: "60px",
            borderRadius: "8px",
            objectFit: "cover",
            flexShrink: 0,
          }}
        />

        {/* TITLE + ARTIST */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: "0.95rem",
            fontWeight: "900",
            color: "#f5f5f5",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}>
            {song?.title || "Loading..."}
          </div>
          <div style={{
            fontSize: "0.75rem",
            color: "#ff8c00",
            fontWeight: "700",
            textTransform: "uppercase",
          }}>
            {song?.artist_name}
          </div>
        </div>

        {/* CONTROLS */}
        <div style={{ display: "flex", gap: "8px", alignItems: "center", flexShrink: 0 }}>
          <button onClick={toggleLike} style={{
            width: "34px",
            height: "34px",
            borderRadius: "999px",
            border: `1px solid ${isLiked ? "#ff2b63" : "rgba(255,140,0,0.5)"}`,
            background: isLiked ? "#ff2b63" : "#111",
            color: "white",
            cursor: "pointer",
            fontSize: "0.85rem",
          }}>♥</button>

          <button onClick={togglePlay} style={{
            width: "40px",
            height: "40px",
            borderRadius: "999px",
            border: "2px solid #ff8c00",
            background: "#181818",
            color: "#fff",
            cursor: "pointer",
            fontSize: "1rem",
            fontWeight: "900",
          }}>
            {isPlaying ? "❚❚" : "▶"}
          </button>

          <button onClick={onNext} style={{
            padding: "6px 10px",
            background: "#111",
            color: "#ff8c00",
            border: "1px solid rgba(255,140,0,0.5)",
            borderRadius: "999px",
            cursor: "pointer",
            fontWeight: "800",
            fontSize: "0.72rem",
          }}>NEXT</button>
        </div>
      </div>

      {/* SEEK BAR */}
      <div style={{ marginTop: "8px" }}>
        <input
          type="range"
          min="0"
          max={duration}
          value={currentTime}
          onChange={handleSeek}
          style={{ width: "100%", accentColor: "#ff8c00" }}
        />
        <div style={{
          fontSize: "0.68rem",
          color: "rgba(255,255,255,0.55)",
          textAlign: "right",
        }}>
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>
      </div>
    </div>
  );
}
return (
  <div
    className="player-container"
    style={{
      position: "relative",
      zIndex: 1100,
      background: "rgba(0,0,0,0.72)",
      border: "1px solid rgba(255,140,0,0.25)",
      boxShadow: "0 0 18px rgba(255,140,0,0.10)",
      padding: "14px",
      borderRadius: "14px",
      color: "white",
      width: "430px",
      maxWidth: "430px",
    }}
  >
    <audio
      ref={audioRef}
      onTimeUpdate={handleTimeUpdate}
      onLoadedMetadata={updateDuration}
      onDurationChange={updateDuration}
      onEnded={onSongEnd}
    />

    <div
      style={{
        display: "flex",
        gap: "14px",
      }}
    >
      {/* LEFT SIDE — ARTWORK */}

	{/* LEFT COLUMN */}
<div
  style={{
    width: "150px",
    minWidth: "150px",
    display: "flex",
    flexDirection: "column",
  }}
>
  {/* ARTWORK */}
  <div
    style={{
      width: "150px",
      height: "150px",
      borderRadius: "10px",
      overflow: "hidden",
      background: "#111",
      border: "1px solid rgba(255,255,255,0.08)",
    }}
  >
    <img
      src={
        song?.song_picture ||
        "https://placehold.co/300x300/111111/ff2b63?text=SIGNALBLEED"
      }
      alt="song artwork"
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
      }}
    />
  </div>

  {/* VOLUME */}
  <div
    style={{
      marginTop: "10px",
    }}
  >
    <div
      style={{
        fontSize: "0.72rem",
        color: "#00d4ff",
        marginBottom: "4px",
        letterSpacing: "0.06rem",
        fontWeight: "800",
      }}
    >
      🔊 VOL
    </div>

    <input
      type="range"
      min="0"
      max="1"
      step="0.01"
      value={volume}
      onChange={(e) => setVolume(Number(e.target.value))}
      style={{
        width: "100%",
        accentColor: "#00d4ff",
        cursor: "pointer",
      }}
    />
  </div>
</div>


      {/* RIGHT SIDE */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {/* TITLE AREA */}
        <div>
          <div
            style={{
              fontSize: "1.15rem",
              fontWeight: "900",
              color: "#f5f5f5",
              marginBottom: "4px",
              lineHeight: "1.15",
            }}
          >
            {song?.title || "Loading..."}
          </div>

          <div
            style={{
              fontSize: "0.82rem",
              color: "#ff8c00",
              letterSpacing: "0.04rem",
              fontWeight: "700",
              textTransform: "uppercase",
            }}
          >
            {song?.artist_name}
          </div>
        </div>

        {/* FAKE WAVEFORM */}
	<div
	style={{
  display: "flex",
  alignItems: "center",
  gap: "1px",
  height: "54px",
  marginTop: "12px",
  marginBottom: "12px",
  overflow: "hidden",
  width: "100%",
  justifyContent: "space-between",
}}
>
	{waveformBars.map((amp, i) => {
	         const isPlayed = i / waveformBars.length <= progressRatio;
                 const offset = i % 2 === 0 ? -1 : 1;
  return (
    <div
      key={i}
      style={{
        width: "1.5px",
        height: `${amp * 38}px`,
        background: isPlayed
          ? "rgba(255, 43, 99, 0.95)"
          : "rgba(255, 140, 0, 0.45)",
        borderRadius: "999px",
        flexShrink: 0,
	transform: `translateY(${offset * amp * 8}px)`,
      }}
    />
  );
})}
</div>

        {/* CONTROLS */}
        <div
         style={{
  display: "grid",
  gridTemplateColumns: "1fr 54px 1fr",
  alignItems: "center",
  gap: "10px",
  marginBottom: "10px",
  width: "100%",
}}
        >
          {/* LIKE */}
          <button
            onClick={toggleLike}
            style={{
    	      width: "100%",
              padding: "9px 10px",
              background: isLiked ? "#ff2b63" : "#111",
              color: "white",
              border: `1px solid ${
                isLiked ? "#ff2b63" : "rgba(255,140,0,0.5)"
              }`,
              borderRadius: "999px",
              cursor: "pointer",
              fontWeight: "800",
              fontSize: "0.72rem",
            }}
          >
            ♥
          </button>

          {/* PLAY */}
          <button
            onClick={togglePlay}
            style={{
              width: "54px",
              height: "54px",
              borderRadius: "999px",
              border: "2px solid #ff8c00",
              background: "#181818",
              color: "#fff",
              cursor: "pointer",
              fontSize: "1rem",
              fontWeight: "900",
            }}
          >
            {isPlaying ? "❚❚" : "▶"}
          </button>

          {/* NEXT */}
          <button
            onClick={onNext}
            style={{
	      width: "100%",
              padding: "9px 10px",
              background: "#111",
              color: "#ff8c00",
              border: "1px solid rgba(255,140,0,0.5)",
              borderRadius: "999px",
              cursor: "pointer",
              fontWeight: "800",
              fontSize: "0.72rem",
            }}
          >
            NEXT
          </button>
        </div>

        {/* SEEK BAR */}
        <div>
          <input
            type="range"
            min="0"
            max={duration}
            value={currentTime}
            onChange={handleSeek}
            style={{
              width: "100%",
              marginBottom: "4px",
            }}
          />

          <div
            style={{
              fontSize: "0.72rem",
              color: "rgba(255,255,255,0.72)",
            }}
          >
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        </div>
      </div>
    </div>
  </div>
);




};
export default HLSPlayer;
